export type DraftPrimaryAction =
	| "chooseFromShortlist"
	| "finishDraft"
	| "resultsOnly"
	| "toNextUserPick";

export const draftDecisionCompareLimit = 3;

export const getDraftPrimaryAction = ({
	draftIsOver,
	spectator,
	userRemaining,
	usersTurn,
}: {
	draftIsOver: boolean;
	spectator: boolean;
	userRemaining: boolean;
	usersTurn: boolean;
}): DraftPrimaryAction => {
	if (draftIsOver) {
		return "resultsOnly";
	}
	if (usersTurn && !spectator) {
		return "chooseFromShortlist";
	}
	if (userRemaining && !spectator) {
		return "toNextUserPick";
	}
	return "finishDraft";
};

export const getDraftProspectPreview = <T,>(prospects: T[], limit: number) => ({
	hidden: prospects.slice(limit),
	hiddenCount: Math.max(0, prospects.length - limit),
	visible: prospects.slice(0, limit),
});

type DraftDecisionPositionPlayer = {
	ratings?: {
		ovr?: number;
		pos?: string;
		pot?: number;
	};
};

const DRAFT_TARGET_DEPTH_BY_POSITION: Record<string, number> = {
	C: 2,
	PF: 2,
	PG: 2,
	SF: 2,
	SG: 2,
};

export const getDraftTeamNeedSummary = <
	TRosterPlayer extends DraftDecisionPositionPlayer,
	TProspect extends DraftDecisionPositionPlayer,
>({
	limit = 3,
	prospects,
	rosterPlayers,
}: {
	limit?: number;
	prospects: TProspect[];
	rosterPlayers: TRosterPlayer[];
}) => {
	const rosterCounts = new Map<string, number>();
	for (const player of rosterPlayers) {
		const pos = player.ratings?.pos;
		if (pos) {
			rosterCounts.set(pos, (rosterCounts.get(pos) ?? 0) + 1);
		}
	}

	const candidatePositions = [
		...new Set(
			prospects
				.map((player) => player.ratings?.pos)
				.filter((pos): pos is string => !!pos),
		),
	];
	const positions = Object.entries(DRAFT_TARGET_DEPTH_BY_POSITION)
		.map(([pos, target]) => ({
			count: rosterCounts.get(pos) ?? 0,
			pos,
			target,
		}))
		.filter(({ count, target }) => count < target)
		.sort((a, b) => a.count - b.count || a.pos.localeCompare(b.pos))
		.map(({ pos }) => pos)
		.slice(0, limit);

	if (positions.length < limit) {
		const fallbackPositions = [...new Set([...candidatePositions, ...rosterCounts.keys()])];
		for (const pos of fallbackPositions.sort(
			(a, b) => (rosterCounts.get(a) ?? 0) - (rosterCounts.get(b) ?? 0),
		)) {
			if (!positions.includes(pos)) {
				positions.push(pos);
			}
			if (positions.length >= limit) {
				break;
			}
		}
	}

	return {
		positions,
		rosterCounts,
	};
};

export type DraftProspectRisk = "hidden" | "low" | "medium" | "high";

export const getDraftProspectRisk = ({
	challengeNoRatings,
	ovr,
	pot,
}: {
	challengeNoRatings: boolean;
	ovr?: number;
	pot?: number;
}): DraftProspectRisk => {
	if (challengeNoRatings || ovr === undefined || pot === undefined) {
		return "hidden";
	}

	const developmentGap = pot - ovr;
	if (developmentGap >= 18) {
		return "high";
	}
	if (developmentGap >= 10) {
		return "medium";
	}
	return "low";
};
